import React, { useContext, useEffect, useState } from "react";
import "./course-details-header.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import PagesHeadComponent from "../../../../../components/pages-head-component/pages-head.component";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../../../store";
import { UserRoles } from "../../../../../types/userRoles";
import ActionMenuComponent from "../../../../reusable-components/components/menu.component";
import { CreateSectionDialog } from "../../../modals/create-section/create-section.dialog";
import CreateCourseDialog from "../../../modals/create-course/create-course.dialog";
import { ConfirmDialog } from "../../../../../dialogs/confirm-dialog/confirm-dialog";
import { useDocumentUploader } from "../../../../../store/hooks/useFileUploader";
import { fileTypes } from "../../../../../statics/file-types";
import { services } from "../../../../../services";
import {
  formatDate,
  showPopupToastMsg,
  showToastMsg,
} from "../../../../../helpers";
import { history } from "react-router-guard";
import { Skeleton } from "@material-ui/lab";
import { icons } from "../../../../../statics/icons";
import AppChipsComponent from "../../../../../components/form/app-chips";
import RefreshCounterComponent from "../../../../../components/refresh-counter/refresh-counter.component";
import { educationRoutes } from "../../../types/education.routes";
import { CourseStatus } from "../../../types/courses.types";

const CourseDetailsHeaderComponent = ({
  courseDetails,
  loading,
  onFetchCourseDetails,
  onSectionCreated,
  sections,
}: {
  courseDetails: any;
  loading: boolean;
  onFetchCourseDetails: () => void;
  onSectionCreated: () => void;
  sections: any[];
}) => {
  const { t } = useTranslation();
  const { user } = useContext(GlobalContext);

  const [courseData, setCourseData] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateSectionDialog, setShowCreateSectionDialog] = useState(false);
  const [cardsData, setCardsData] = useState<any[]>([]);

  // ---------- Upload Document ----------
  const { openUploader, UploadUI } = useDocumentUploader({
    accept: fileTypes,
    path: `courses`,
    segment: courseData ? `${courseData.id}` : "",
    onUpload: async (file) => {
      const response = await services.courses.addDocument(
        courseData ? courseData.id : "",
        file,
      );
      if (response && response.message === "SUCCESS") {
        showToastMsg("success", t("DOCUMENT_ADDED_SUCCESSFULLY"));
        onFetchCourseDetails();
      } else {
        showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
      }
    },
  });
  // ---------- Upload Document ----------

  const handleConfirmDelete = async (confirmed: boolean) => {
    setTimeout(() => {
      setShowConfirmModal(false);
    }, 0);
    if (confirmed) {
      deleteCourse();
    }
  };

  const deleteCourse = async () => {
    const response = await services.courses.delete(courseData.id);
    if (response && response.message === "SUCCESS") {
      showToastMsg("success", t("EDUCATION_PAGE.COURSE_DELETED_SUCCESSFULLY"));
      history.push(educationRoutes.courses);
    } else if (response.message) {
      showPopupToastMsg("error", t(response.message));
    } else {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
  };

  const handleEditModalClose = (value: any) => {
    setTimeout(() => {
      setShowEditModal(false);
    }, 0);
    if (value) {
      onFetchCourseDetails();
    }
  };

  useEffect(() => {
    setCourseData(courseDetails);
    console.log("Here in course details header");
    setCardsData([
      {
        icon: icons.PAGES.EDUCATION.VIDEO_FILL,
        title: "EDUCATION_PAGE.TOTAL_VIDEOS",
        value: sections?.reduce(
          (acc: number, section: any) => acc + (section.lessons?.length ?? 0),
          0,
        ),
      },
      {
        icon: icons.PAGES.EDUCATION.UNITS,
        title: "EDUCATION_PAGE.TOTAL_UNITS",
        value: sections?.length ?? 0,
      },
      {
        icon: icons.PAGES.EDUCATION.FILES,
        title: "EDUCATION_PAGE.TOTAL_FILES",
        value: courseDetails?.documents?.length ?? 0,
      },
    ]);
  }, [sections, courseDetails]);

  return (
    <Box className="">
      {/* Start Top Header */}
      <Box
        className="align-items-center"
        style={{ display: "grid", gridTemplateColumns: "1fr .1fr" }}
      >
        <PagesHeadComponent
          loading={loading}
          title={t("PAGES_HEAD.COURSE_DETAILS_TITLE")}
          subTitle={t("PAGES_HEAD.COURSE_DETAILS_SUB_TITLE")}
        />
        {UserRoles.FULL_ACCESS_ROLES.includes(user.role) && (
          <Box className="text-end">
            <ActionMenuComponent
              items={[
                {
                  label: t("EDIT"),
                  onClick: () => setShowEditModal(true),
                },
                {
                  label: t("DELETE"),
                  onClick: () => setShowConfirmModal(true),
                },
                {
                  label: t("EDUCATION_PAGE.CREATE_SECTION"),
                  onClick: () => setShowCreateSectionDialog(true),
                },
                {
                  label: t("EDUCATION_PAGE.UPLOAD_DOCUMENTS"),
                  onClick: () => openUploader(),
                },
              ]}
            />
            <UploadUI />
            {showConfirmModal && (
              <ConfirmDialog
                show={showConfirmModal}
                onClose={handleConfirmDelete}
                dialogTitle={t("EDUCATION_PAGE.CONFIRM_DELETE")}
                dialogBody={t(
                  "EDUCATION_PAGE.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_COURSE",
                )}
                confirmBtnText={t("DELETE")}
                cancelBtn={true}
              />
            )}

            {showCreateSectionDialog && (
              <CreateSectionDialog
                show={showCreateSectionDialog}
                courseId={Number(courseData.id)}
                onClose={(success) => {
                  setShowCreateSectionDialog(false);
                  if (success) onSectionCreated();
                }}
              />
            )}

            {showEditModal && (
              <CreateCourseDialog
                show={showEditModal}
                onClose={handleEditModalClose}
                courseId={Number(courseData.id)}
                courseData={courseData}
              />
            )}
          </Box>
        )}
      </Box>
      {/* End Top Header */}
      <Box className="grid-2 mb-4" style={{ gap: "1.9rem" }}>
        {/* Start Course Details Header */}
        <Box className="info__content">
          <Box className="d-flex" style={{ gap: "0.5rem" }}>
            {!loading &&
            courseData?.userStatus?.status === CourseStatus.COMPLETED ? (
              <Box className="d-flex">
                <img
                  src={icons.PAGES.EDUCATION.COMPLETED}
                  alt="completed"
                  width={40}
                  height={40}
                />
              </Box>
            ) : null}
            <Box className="info">
              {loading ? (
                <Skeleton variant="text" height={20} width={350} />
              ) : (
                <Typography variant="h2">{courseData.title}</Typography>
              )}
              {loading ? (
                <Skeleton variant="text" height={18} width={500} />
              ) : (
                <Typography variant="h4">{courseData.brief}</Typography>
              )}
            </Box>
          </Box>
          {loading ? (
            <Skeleton variant="text" height={15} width={150} />
          ) : (
            <Typography
              variant="body1"
              color="textSecondary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                margin: "0.5rem 0",
              }}
            >
              <img src={icons.CALENDER} alt="calender" />{" "}
              {formatDate(courseData.createdAt, "ddd, MMM DD, HH:mm Z")}
            </Typography>
          )}
          {loading ? (
            <Skeleton
              variant="rect"
              height={32}
              width={100}
              style={{ borderRadius: "6px" }}
            />
          ) : (
            <AppChipsComponent
              label={courseData?.category?.name}
              color="secondary"
              variant="default"
              size="medium"
            />
          )}
        </Box>
        {/* End Course Details Header */}
        {loading ? (
          <Box className="items">
            <Skeleton
              variant="rect"
              height={150}
              width="100%"
              style={{ borderRadius: "12px" }}
            />
            <Skeleton
              variant="rect"
              height={150}
              width="100%"
              style={{ borderRadius: "12px" }}
            />
            <Skeleton
              variant="rect"
              height={150}
              width="100%"
              style={{ borderRadius: "12px" }}
            />
          </Box>
        ) : (
          <Box className="items">
            {cardsData.map((item, index: number) => {
              return (
                <Box className="item" key={index}>
                  <Box
                    className="global__box__content global__box__content_bg"
                    style={{ padding: "15px" }}
                  >
                    <Box
                      className="d-flex flex-column justify-content-between"
                      style={{ height: "calc(150px - 30px)" }}
                    >
                      <img width={22} src={item.icon} alt="" />
                      <Box className="w-100">
                        <Typography
                          variant="subtitle1"
                          className="font-weight-bold mb-2"
                        >
                          <RefreshCounterComponent targetNumber={item.value} />
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t(item.title)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CourseDetailsHeaderComponent;
